'use client';

import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { type FC, useEffect } from 'react';

import Icon from 'molecules/Icon';
import RichText from 'molecules/RichText';

import styles from 'components/FAQPage/faqpage.module.scss';

import { isNonEmptyArray, stringToId } from 'utils/functions';

import type { FAQPageProps } from 'components/FAQPage/config';
import type { FormEvent } from 'react';

const FAQPage: FC<FAQPageProps> = ({ faqPages }) => {
  const searchParams = useSearchParams();
  const router = useRouter();

  const smoothScroll = (id: string, scroll = true) => {
    const element = document.getElementById(id);
    if (element) {
      const navbarOffset = 100;
      const elementPosition = element.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({ top: elementPosition - navbarOffset, behavior: scroll ? 'smooth' : 'auto' });
    }
  };

  const handleSmoothScroll = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>, id: string) => {
    event.preventDefault();
    smoothScroll(id);
  };

  useEffect(() => {
    const faq = searchParams.get('faq');

    if (faq) {
      smoothScroll(faq, false);
    }
  }, [searchParams]);

  const faqSection = isNonEmptyArray(faqPages)
    ? faqPages.flatMap(page => page.faqPage || []).find(faqList => faqList.type === 'faqs') || {
        heading: '',
        link: '',
        faqs: [],
      }
    : { heading: '', link: '', faqs: [] };

  const currentPageHeading = faqSection?.heading || 'FAQs';
  const currentPageLink = faqSection?.link || '/faq';

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const search = formData.get('search');

    if (search) {
      const searchValue = search;
      router.push(`/search?q=${searchValue}`);
    }
  };

  return (
    <>
      <div className={styles.headerStyle}>
        <div className={styles.link}>
          <a href="/faq">FAQ</a>
          <span>/</span>
          <a href={currentPageLink}>{currentPageHeading}</a>
        </div>
        <div className={styles.heading}>
          <h1>{currentPageHeading}</h1>
        </div>
      </div>
      <div className={styles.container}>
        <div className={styles.accordionFaq}>
          {isNonEmptyArray(faqPages) &&
            faqPages.map((faqpage, index) => (
              <div className={styles.borderBottom} key={index}>
                {faqpage.heading && <div className={styles.title}>{faqpage.heading}</div>}
                <div className={styles.accordion}>
                  {isNonEmptyArray(faqpage.faqPage) &&
                    faqpage.faqPage.map(faqList => (
                      <>
                        {faqList.type === 'link' ? (
                          <Link tabIndex={0} id={faqList.link} className={styles.heading} href={faqList.link || ''}>
                            {faqList.heading}
                          </Link>
                        ) : (
                          <div className={styles.activeHeading}>{faqList.heading}</div>
                        )}

                        {faqList.type === 'faqs' && isNonEmptyArray(faqList.faqs) && (
                          <div className={styles.faqPad}>
                            {faqList.faqs.map((faq, index2) => (
                              <li className={styles.faq} key={index2}>
                                <a
                                  href={`#${stringToId(faq.question)}`}
                                  onClick={event => handleSmoothScroll(event, stringToId(faq.question))}
                                >
                                  {faq.question}
                                </a>
                              </li>
                            ))}
                          </div>
                        )}
                      </>
                    ))}
                </div>
              </div>
            ))}
          <div className={styles.formWrapper} id="search">
            <form onSubmit={handleSearch}>
              <input
                type="text"
                name="search"
                id="search"
                aria-label="Search for an entry"
                className={styles.textField}
              />
              <button className={styles.searchButton} type="submit" aria-label="Submit search">
                <Icon icon="icon-search" size={26} className={styles.iconColor} />
              </button>
            </form>
          </div>
        </div>
        <div className={styles.body}>
          {isNonEmptyArray(faqPages) &&
            faqPages
              .flatMap(page => page.faqPage || [])
              .map(
                faqList =>
                  faqList.type === 'faqs' &&
                  faqList.faqs?.map((faq, index) => (
                    <div className={styles.faq} key={index}>
                      {faq.question && (
                        <div id={stringToId(faq.question)} className={styles.question}>
                          {faq.question}
                        </div>
                      )}
                      {faq.answer && <RichText className={styles.answer} content={faq.answer.replace(/\\n/g, '\n')} />}
                    </div>
                  )),
              )}
        </div>
      </div>
    </>
  );
};
export default FAQPage;
